import { getApiConfig } from "../config";
import { proxyFetch } from "./proxyFetch";

const BATCH_PATH = "/marketdata/option-quotes-batch";
const CLIENT_CACHE_MS = 5 * 60 * 1000;
const CLIENT_MISS_CACHE_MS = 2 * 60 * 1000;

function marketdataBatchUrl(): string | null {
  const { baseUrl } = getApiConfig();
  if (!baseUrl) return null;
  if (baseUrl === window.location.origin) return BATCH_PATH;
  return `${baseUrl}${BATCH_PATH}`;
}

export interface MarketdataOptionQuote {
  mid: number | null;
  bid: number | null;
  ask: number | null;
  last: number | null;
  pop: number | null;
  cached?: boolean;
  stale?: boolean;
}

export interface MarketdataOptionQuotesBatchResult {
  ok: boolean;
  quotes: Record<string, MarketdataOptionQuote>;
  creditsUsed: number;
  symbolsFetched: number;
  dailyCreditsRemaining?: number;
  code?: string;
  message?: string;
}

const clientQuoteCache = new Map<
  string,
  { quote: MarketdataOptionQuote | null; at: number; miss?: boolean }
>();

function marketdataHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

function normalizeOptionSymbol(symbol: string): string {
  return String(symbol || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function getCachedMarketdataOptionQuote(
  symbol: string,
): MarketdataOptionQuote | null | undefined {
  const key = normalizeOptionSymbol(symbol);
  if (!key) return undefined;
  const hit = clientQuoteCache.get(key);
  if (!hit) return undefined;
  const ttl = hit.miss ? CLIENT_MISS_CACHE_MS : CLIENT_CACHE_MS;
  if (Date.now() - hit.at > ttl) {
    clientQuoteCache.delete(key);
    return undefined;
  }
  return hit.quote;
}

export function rememberMarketdataOptionQuote(
  symbol: string,
  quote: MarketdataOptionQuote | null | undefined,
  miss = false,
): void {
  const key = normalizeOptionSymbol(symbol);
  if (!key) return;
  const empty =
    !quote ||
    (quote.mid == null && quote.last == null && quote.bid == null && quote.ask == null);
  clientQuoteCache.set(key, {
    quote: quote ?? null,
    at: Date.now(),
    miss: miss || empty,
  });
}

export async function fetchMarketdataOptionQuotesBatch(
  symbols: string[],
  force = false,
): Promise<MarketdataOptionQuotesBatchResult> {
  const empty: MarketdataOptionQuotesBatchResult = {
    ok: false,
    quotes: {},
    creditsUsed: 0,
    symbolsFetched: 0,
  };

  const normalized = [...new Set(symbols.map(normalizeOptionSymbol).filter(Boolean))];
  if (!normalized.length) {
    return { ...empty, code: "invalid_params" };
  }

  const batchUrl = marketdataBatchUrl();
  if (!batchUrl) {
    return { ...empty, code: "proxy_unconfigured" };
  }

  const quotes: Record<string, MarketdataOptionQuote> = {};
  const missing: string[] = [];
  if (!force) {
    for (const sym of normalized) {
      const cached = getCachedMarketdataOptionQuote(sym);
      if (cached !== undefined) {
        if (cached) quotes[sym] = { ...cached, cached: true };
      } else {
        missing.push(sym);
      }
    }
    if (!missing.length) {
      return { ok: true, quotes, creditsUsed: 0, symbolsFetched: 0 };
    }
  } else {
    missing.push(...normalized);
  }

  try {
    const res = await proxyFetch(batchUrl, {
      method: "POST",
      headers: marketdataHeaders(),
      body: JSON.stringify({ symbols: missing, force }),
      cache: "no-store",
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ...empty,
        quotes,
        code: String(data.code ?? "http_error"),
        message: String(data.message ?? `${res.status} ${res.statusText}`),
      };
    }

    const fetched =
      data.quotes && typeof data.quotes === "object"
        ? (data.quotes as Record<string, MarketdataOptionQuote>)
        : {};
    for (const [sym, quote] of Object.entries(fetched)) {
      rememberMarketdataOptionQuote(sym, quote, !quote);
      if (quote) quotes[normalizeOptionSymbol(sym)] = quote;
    }
    for (const sym of missing) {
      if (quotes[sym]) continue;
      rememberMarketdataOptionQuote(sym, null, true);
    }

    return {
      ok: Boolean(data.ok),
      quotes,
      creditsUsed: Number(data.creditsUsed) || 0,
      symbolsFetched: Number(data.symbolsFetched) || 0,
      dailyCreditsRemaining:
        data.dailyCreditsRemaining != null ? Number(data.dailyCreditsRemaining) : undefined,
      code: data.code != null ? String(data.code) : undefined,
      message: data.message != null ? String(data.message) : undefined,
    };
  } catch (err) {
    return {
      ...empty,
      quotes,
      code: "network_error",
      message: err instanceof Error ? err.message : "fetch failed",
    };
  }
}
